import React, { useState } from 'react';

interface RSSItem {
  link?: string;
  label: string;
  subLabel?: string;
  icon?: string;
  color?: string;
  isImage?: boolean;
  imageSrc?: string;
  mobileImageSrc?: string;
  isCopy?: boolean;
  copyValue?: string;
  hideLabel?: boolean;
}

interface CrossPlatformRSSProps {
  items: RSSItem[];
}

const CrossPlatformRSS: React.FC<CrossPlatformRSSProps> = ({ items }) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 0',
    width: '100%',
    boxSizing: 'border-box',
    flexWrap: 'wrap',
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
  };

  const baseButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    height: '36px', // Increased slightly to match Apple's larger footprint
    padding: '0 14px',
    borderRadius: '10px',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '0.75rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxSizing: 'border-box',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: 'inherit',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
  };

  const handleItemClick = (e: React.MouseEvent, item: RSSItem, index: number) => {
    if (item.isCopy && item.copyValue) {
      e.preventDefault();
      const fullUrl = item.copyValue.startsWith('http') 
        ? item.copyValue 
        : window.location.origin + item.copyValue;
      
      navigator.clipboard.writeText(fullUrl).then(() => {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      });
    }
  };

  return (
    <>
      <style>
        {`
          .cp-rss-button {
            border: 1px solid rgba(255, 255, 255, 0.1);
          }
          .cp-rss-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            filter: brightness(1.1);
          }
          .cp-rss-button.copied {
            background-color: #4caf50 !important;
            transform: scale(0.98);
          }
          .cp-mobile-only {
            display: none !important;
          }
          .cp-desktop-only {
            display: block !important;
          }
          
          .cp-text-container {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            line-height: 1.0;
          }
          .cp-label-small {
            font-size: 0.48rem; /* Even smaller */
            font-weight: 500;
            opacity: 0.9;
            margin-bottom: 2px;
            letter-spacing: 0.02em;
          }
          .cp-sublabel-big {
            font-size: 0.95rem; /* Even bigger */
            font-weight: 700;
            letter-spacing: -0.01em;
          }
          .cp-icon-large {
            font-size: 1.6rem !important; /* Larger to match double lines */
          }
          
          @media (max-width: 480px) {
            .cp-rss-button {
              height: 30px !important;
              font-size: 0.65rem !important;
              padding: 0 8px !important;
              gap: 6px !important;
              border-radius: 8px !important;
            }
            .cp-rss-icon {
              font-size: 0.85rem !important;
            }
            .cp-icon-large {
              font-size: 1.3rem !important;
            }
            .cp-label-small {
              font-size: 0.4rem !important;
              margin-bottom: 1px !important;
            }
            .cp-sublabel-big {
              font-size: 0.8rem !important;
            }
            .cp-mobile-only {
              display: block !important;
              height: 30px !important; 
            }
            .cp-desktop-only {
              display: none !important;
            }
          }
        `}
      </style>
      <div style={containerStyle}>
        {items.map((item, index) => {
          const isCopied = copiedIndex === index;
          const isTransparent = item.color === 'transparent' || !item.color;
          const isApple = item.imageSrc && item.imageSrc.includes('apple');
          const hasDoubleLines = !!item.subLabel && !isCopied;
          
          const content = (
            <>
              {item.isImage ? (
                <>
                  {item.mobileImageSrc ? (
                    <>
                      <img 
                        src={item.mobileImageSrc} 
                        alt={item.label} 
                        className="cp-rss-image cp-mobile-only"
                        style={{ height: '30px', width: 'auto', objectFit: 'contain' }}
                      />
                      <img 
                        src={item.imageSrc} 
                        alt={item.label} 
                        className="cp-rss-image cp-desktop-only"
                        style={{ height: '36px', width: 'auto', objectFit: 'contain' }}
                      />
                    </>
                  ) : (
                    <img 
                      src={item.imageSrc} 
                      alt={item.label} 
                      className="cp-rss-image"
                      style={{ height: '24px', width: 'auto', objectFit: 'contain' }}
                    />
                  )}
                </>
              ) : (
                <i className={`${isCopied ? 'fi fi-rr-check' : (item.icon)} ${hasDoubleLines ? 'cp-icon-large' : ''} cp-rss-icon`} style={{ fontSize: '1rem' }}></i>
              )}
              {(!item.hideLabel || isCopied) && (
                <div className="cp-text-container">
                  {isCopied ? (
                    <span>Copied!</span>
                  ) : (
                    <>
                      {item.subLabel ? (
                        <>
                          <span className="cp-label-small">{item.label}</span>
                          <span className="cp-sublabel-big">{item.subLabel}</span>
                        </>
                      ) : (
                        <span>{item.label}</span>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          );

          return (
            <div key={index} style={itemStyle}>
              {item.isCopy ? (
                <button
                  type="button"
                  className={`cp-rss-button ${isCopied ? 'copied' : ''}`}
                  onClick={(e) => handleItemClick(e, item, index)}
                  style={{
                    ...baseButtonStyle,
                    backgroundColor: item.color || '#1a1a1a',
                    color: 'white',
                    border: 'none',
                  }}
                  title={item.label}
                >
                  {content}
                </button>
              ) : (
                <a 
                  href={item.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="cp-rss-button"
                  style={{
                    ...baseButtonStyle,
                    backgroundColor: item.color || '#1a1a1a',
                    color: 'white',
                    padding: isApple ? '0' : baseButtonStyle.padding,
                    border: isTransparent ? 'none' : baseButtonStyle.border,
                    boxShadow: isTransparent ? 'none' : '0 3px 8px rgba(0,0,0,0.1)',
                    borderRadius: isApple ? '0' : baseButtonStyle.borderRadius,
                  }}
                  title={item.label}
                >
                  {content}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CrossPlatformRSS;
